<template>
  <f7-popup ref="popup" :opened="open" close-on-escape @popup:close="$emit('update:open', false)" class="tutor-popup">
    <f7-page>
      <f7-navbar>
        <f7-nav-left>
          <f7-link popup-close icon-f7="xmark"></f7-link>
        </f7-nav-left>
        <f7-nav-title>🤖 Tutor IA · {{ moduloLabel }}</f7-nav-title>
        <f7-nav-right>
          <f7-link @click="clearChat" icon-f7="trash" color="gray"></f7-link>
        </f7-nav-right>
      </f7-navbar>

      <!-- Selector de modo -->
      <f7-toolbar tabbar position="top" no-shadow>
        <f7-link
          v-for="m in modes"
          :key="m.key"
          :tab-link-active="mode === m.key"
          @click="setMode(m.key)"
        >{{ m.icon }} {{ m.label }}</f7-link>
      </f7-toolbar>

      <f7-page-content class="tutor-content" ref="contentRef">
        <!-- Bienvenida -->
        <f7-block v-if="messages.length === 0" class="tutor-welcome">
          <p>{{ welcomeText }}</p>
          <div class="tutor-sugs">
            <f7-button
              v-for="s in currentSugs"
              :key="s"
              small
              outline
              round
              class="tutor-sug"
              @click="sendSuggestion(s)"
            >{{ s }}</f7-button>
          </div>
        </f7-block>

        <!-- Mensajes -->
        <div v-for="(msg, i) in messages" :key="i" :class="['tutor-msg', 'tutor-msg-' + msg.role]">
          <div class="tutor-msg-avatar">{{ msg.role === 'user' ? '👤' : '🤖' }}</div>
          <div class="tutor-msg-bubble" v-html="renderMsg(msg.content)"></div>
        </div>

        <!-- Pensando -->
        <div v-if="thinking" class="tutor-msg tutor-msg-assistant">
          <div class="tutor-msg-avatar">🤖</div>
          <div class="tutor-msg-bubble tutor-thinking">
            <f7-preloader size="16"></f7-preloader> Pensando...
          </div>
        </div>
      </f7-page-content>

      <!-- Input -->
      <div class="tutor-input-bar">
        <div class="tutor-input-wrap">
          <textarea
            ref="inputRef"
            v-model="input"
            class="tutor-input"
            rows="1"
            placeholder="Escribe tu pregunta..."
            @keydown.enter.exact.prevent="sendMessage"
          ></textarea>
          <f7-button fill round small class="tutor-send" @click="sendMessage" :disabled="!input.trim() || thinking">
            <f7-icon f7="arrow_up" size="16"></f7-icon>
          </f7-button>
        </div>
      </div>
    </f7-page>
  </f7-popup>
</template>

<script>
import TutorIa from '../../../js/db/TutorIa.js';
import cerrojoDeLaSesion from '../../../js/utils/cerrojoDeLaSesion.js';

const MOD_LABELS = {
  rebt: 'REBT', gasol: 'Gasolineras', cp: 'Comunidades REBT', rebtpf: 'Fotovoltaica',
  ae: 'Ascensores', eiciae: 'EICIAE', aegt: 'Gruas Torre', aega: 'Gruas Autopropulsadas',
  apcal: 'Calderas', apcom: 'Compresores', rite: 'RITE', piripci: 'PCI RIPCI',
  rsciei: 'PCI RSCIEI', ip: 'Petroliferas', sm: 'Maquinas', smex: 'Exp. Tecnicos',
  smmin: 'Mineria', estan: 'Estanterias', ldv: 'Lineas de Vida',
  reatsubest: 'REAT Subestacion', reatct: 'REAT CT', reatlat: 'REAT Lineas AT'
};

/**
 * Contexto tarifario por materia. Lo normativo va literal; lo que vive en la configuracion global
 * —precio hora, kilometros minimos, coste por kilometro— va como marca y se sustituye con el valor
 * vigente al armar el prompt (#379).
 *
 * Cablear esos importes en el texto es el mismo defecto que esta tarea arregla en el calculo: el
 * `ph=106` de aqui seguia afirmandose aunque direccion hubiera puesto otro precio, asi que el tutor
 * explicaba una tarifa que no era la que se estaba cobrando. Al no haber numero que mantener, no
 * puede volver a quedarse atras.
 */
const MOD_CTX = {
  rebt: 'RD 842/2002 REBT. ITC-BT-05. TPC=30+cuadros*20+sup*0.002+farolas*5. ph={PH} EUR/h. km min {KMMIN}.',
  gasol: 'RD 842/2002 ITC-BT-29 Clase I. TPC base 90min+surtidores*10. ph={PH} EUR/h.',
  cp: 'RD 842/2002 ITC-BT-17. TPC: <=25viv=70min, 26-50=80, >100=120. ph={PH} EUR/h.',
  rebtpf: 'RD 842/2002 ITC-BT-40. TPC=(inv_calc+C)*10+40*J. ph={PH} EUR/h.',
  reatsubest: 'RD 337/2014 REAT. TPC=120min trafo principal+45 adicional. ph={PH} EUR/h.',
  reatct: 'RD 337/2014 REAT ITC-RAT. TPC=55*nCT+20*nAd. ph={PH} EUR/h.',
  reatlat: 'RD 223/2008 RLAT ITC-LAT. TPC=45min/km linea. ph={PH} EUR/h.',
  ae: 'RD 355/2024. TPC=55min/ud. TPI=5min/ud. ph={PH} EUR/h. 2/4 anos.',
  aegt: 'RD 836/2003 ITC-MIE-AEM 2. TPC 60-180min. ph={PH} EUR/h. Periodicidad 2 anos.',
  aega: 'RD 837/2003 ITC-MIE-AEM 4. TPC 150min con plumin / 210min sin. ph={PH} EUR/h.',
  rite: 'RD 1027/2007 RITE. TPC=70-135min segun potencia. ph={PH} EUR/h.',
  apcal: 'RD 2060/2008 ITC-EP-1. TPC=180+((PxV/5000)-1)*10. ph={PH} EUR/h.',
  apcom: 'RD 2060/2008 ITC-EP-6. Nivel A=30min, Nivel B=60+((PxV/1200)-1)*8. ph={PH} EUR/h.',
  ip: 'RD 1523/1999 MI-IP02/03/04. TPC 90-135min. ph={PH} EUR/h.',
  piripci: 'RD 513/2017 RIPCI. TPC por superficie y sistemas. ph={PH} EUR/h. 10 anos.',
  rsciei: 'RD 164/2025 RSCIEI. NRI*50+Sectores*60+(m2/800)*60. ph={PH} EUR/h.',
  sm: 'RD 1215/1997. Directiva 2006/42/CE. TPC=N*40min. TPI=18%. ph={PH} EUR/h.',
  smex: 'Dir. 2006/42/CE. RD 1644/2008. TPC manual segun complejidad. ph={PH} EUR/h.',
  smmin: 'RD 863/1985 RGNBSM. Pala=60min, Volquete=70min. ph={PH} EUR/h.',
  estan: 'RD 1597/2021. TPC=40+12*AL+8*MOD<=5m+10*MOD>5m. ph={PH} EUR/h.',
  ldv: 'UNE-EN 795. UNE-EN 365. metros*16+anclajes*38+km*{KMCOST}. Sin 2a inspeccion.',
  eiciae: 'RD 88/2013. TPC=N*30+N*30. ph={PH} EUR/h. Sin 2a inspeccion.'
};

/**
 * Las marcas de MOD_CTX, con la frase que las contiene por si el valor no esta disponible: antes de
 * afirmar un importe que no se sabe, se quita la frase entera.
 *
 * Las frases van como texto literal, no como expresion regular: son fragmentos fijos de MOD_CTX, y
 * un patron que empiece por `\s*` es busqueda con retroceso sobre una cadena que crece con la tabla
 * (Sonar S5852). Con `split`/`join` se quita y se sustituye sin patron ninguno.
 */
const MARCAS_CFG = [
  { marca: '{PH}', campo: 'ph', frase: ' ph={PH} EUR/h.' },
  { marca: '{KMMIN}', campo: 'kmmin', frase: ' km min {KMMIN}.' },
  { marca: '{KMCOST}', campo: 'kmcost', frase: '+km*{KMCOST}' }
];

const SUGS = {
  tecnico: ['Cuales son los defectos mas graves?', 'Explicame el checklist de inspeccion', 'Tiempos minimos ENAC para esta materia', 'Dame un caso practico real'],
  comercial: ['Argumentos de valor para la oferta', 'Como justificar el precio ante el cliente', 'Simula una llamada de captacion en frio', 'Gestiona la objecion "es muy caro"'],
  normativo: ['Articulo exacto que obliga a inspeccionar', 'Periodicidad y plazos reglamentarios', 'Sanciones por incumplimiento', 'Diferencias de aplicacion entre CCAA']
};

const MODE_SYSTEM = {
  tecnico: 'MODO TECNICO: cita articulos exactos. Clasifica defectos DL/DG/DMG con plazos de correccion. Usa listas cuando clarifiquen.',
  comercial: 'MODO COMERCIAL: argumentos de valor, scripts de llamada, roleplay cliente dificil, gestion de objeciones. Los 5 escudos SIMECAL: ENAC Tipo A, 25 anos, informe mismo dia, 65 personas, servicio integral.',
  normativo: 'MODO NORMATIVO: referencias exactas (articulo+RD+ITC). Sanciones reales con importes. Diferencias entre CCAA cuando aplica.'
};

export default {
  props: {
    modulo: { type: String, required: true },
    open: { type: Boolean, default: false },
    // La configuracion EFECTIVA que esta usando el calculador —la global con el override de la
    // herramienta ya aplicado, hoy solo los kilometros minimos—, para que el tutor hable de la misma
    // tarifa que se esta cobrando y no de la de otra herramienta (#379).
    cfg: { type: Object, default: null }
  },
  data: function() {
    return {
      mode: 'tecnico',
      modes: [
        { key: 'tecnico', icon: '🔬', label: 'Tecnico' },
        { key: 'comercial', icon: '💼', label: 'Comercial' },
        { key: 'normativo', icon: '⚖️', label: 'Normativo' }
      ],
      messages: [],
      input: '',
      thinking: false
    };
  },
  computed: {
    moduloLabel: function() { return MOD_LABELS[this.modulo] || this.modulo.toUpperCase(); },
    welcomeText: function() {
      const m = this.moduloLabel;
      if (this.mode === 'comercial') return 'Hola! Soy tu coach comercial para ' + m + '. Argumentarios, scripts y simulacion de clientes.';
      if (this.mode === 'normativo') return 'Hola! Soy tu experto normativo para ' + m + '. Articulos exactos, plazos y sanciones.';
      return 'Hola! Soy tu tutor tecnico para ' + m + '. Defectos, tiempos ENAC, casos practicos. Por donde empezamos?';
    },
    currentSugs: function() { return SUGS[this.mode] || SUGS.tecnico; },
    /**
     * El contexto tarifario de la materia con los valores de la configuracion global puestos. Lo que
     * no se sabe no se afirma: si falta el dato, se cae la frase que lo citaba.
     */
    referenciaTarifaria: function() {
      const cfg = this.cfg || {};
      let ctx = MOD_CTX[this.modulo] || 'Normativa de seguridad industrial espanola';
      MARCAS_CFG.forEach(function(m) {
        if (ctx.indexOf(m.marca) < 0) return;
        const valor = parseFloat(cfg[m.campo]);
        ctx = isNaN(valor) ? ctx.split(m.frase).join('') : ctx.split(m.marca).join(String(valor));
      });
      return ctx.trim();
    }
  },
  beforeDestroy: function() {
    // La calculadora puede irse con el tutor abierto: sin cerrarlo antes, Framework7 deja la capa
    // gris sobre la pantalla a la que se vuelve (#1027, ver CalculadorToolbar).
    if (this.$refs.popup) this.$refs.popup.close(false);
  },
  watch: {
    open: function(v) {
      if (v) {
        const self = this;
        this.$nextTick(function() {
          // Sin `preventScroll`, el navegador corre la raíz de la aplicación para enseñar la casilla
          // mientras la ventana aún está entrando por abajo, y la pantalla de debajo se descuadra (#467).
          if (self.$refs.inputRef) self.$refs.inputRef.focus({ preventScroll: true });
        });
      }
    }
  },
  methods: {
    setMode: function(m) { this.mode = m; this.messages = []; },
    clearChat: function() { this.messages = []; },
    sendSuggestion: function(text) { this.input = text; this.sendMessage(); },
    scrollDown: function() {
      const self = this;
      this.$nextTick(function() {
        if (self.$refs.contentRef && self.$refs.contentRef.$el) {
          self.$refs.contentRef.$el.scrollTop = self.$refs.contentRef.$el.scrollHeight;
        }
      });
    },
    renderMsg: function(text) {
      if (!text) return '';
      return text
        .replace(/<script[^>]*>.*?<\/script>/gi, '')
        .replace(/\*\*([^*]+)\*\*/g, '<strong>$1</strong>')
        .replace(/\n\n/g, '</p><p>')
        .replace(/\n/g, '<br/>');
    },
    sendMessage: function() {
      const text = this.input.trim();
      if (!text || this.thinking) return;
      this.input = '';
      this.messages.push({ role: 'user', content: text });
      this.scrollDown();
      // #663 · Cerrojo de la sesión: si la sesión cambia en vuelo, la frontera calla el `finally` que lo soltaba.
      cerrojoDeLaSesion(this, 'thinking');
      const self = this;

      const systemPrompt = 'Eres el Tutor IA de SIMECAL (OCA ENAC Tipo A n56/E090), 25 anos de experiencia, 65 personas en el equipo.\n'
        + 'Respondes SIEMPRE en espanol. Max 400 palabras por respuesta. Termina con una accion concreta accionable HOY.\n'
        + 'Nunca inventas normativa. Si no estas seguro, lo dices explicitamente.\n\n'
        + 'MATERIA ACTIVA: ' + self.moduloLabel + '\n'
        + 'REFERENCIA TARIFARIA: ' + self.referenciaTarifaria + '\n\n'
        + (MODE_SYSTEM[self.mode] || '');

      TutorIa.chat({
        data: {
          system: systemPrompt,
          messages: self.messages.slice(-10).map(function(m) { return { role: m.role, content: m.content }; }),
          modulo: self.modulo,
          modo: self.mode
        }
      })
        .then(function(data) {
          const reply = (data.content && data.content[0] && data.content[0].text) || data.texto || data.reply || 'Sin respuesta del servidor. Verifica que simecal-ai esta activo.';
          self.messages.push({ role: 'assistant', content: reply });
          self.scrollDown();
        })
        .catch(function(e) {
          const msg = (typeof e === 'string') ? e : (e.message || 'Error desconocido');
          self.messages.push({ role: 'assistant', content: 'Error de conexion: ' + msg + '. Verifica que simecal-ai esta activo.' });
          self.scrollDown();
        })
        .finally(function() { self.thinking = false; });
    }
  }
};
</script>

<style scoped>
.tutor-popup { --f7-popup-tablet-width:420px; }
.tutor-content { padding-bottom:80px !important; }
.tutor-welcome p { font-size:13px; color:#334155; line-height:1.6; }
.tutor-sugs { display:flex; flex-wrap:wrap; gap:5px; margin-top:8px; }
.tutor-sug { --f7-button-font-size:10px; text-transform:none; }
.tutor-msg { display:flex; gap:8px; align-items:flex-start; padding:6px 16px; }
.tutor-msg-user { flex-direction:row-reverse; }
.tutor-msg-avatar { width:28px; height:28px; border-radius:50%; display:flex; align-items:center; justify-content:center; font-size:14px; background:#e0e7ff; flex-shrink:0; }
.tutor-msg-assistant .tutor-msg-avatar { background:linear-gradient(135deg,#7c3aed,#5b21b6); }
.tutor-msg-bubble { max-width:85%; padding:8px 12px; font-size:12px; line-height:1.55; border-radius:4px 12px 12px 12px; background:#f1f5f9; border:1px solid #e2e8f0; color:#1e293b; }
.tutor-msg-user .tutor-msg-bubble { background:#2563eb; color:#fff; border-color:transparent; border-radius:12px 4px 12px 12px; }
.tutor-msg-bubble >>> strong { font-weight:700; }
.tutor-msg-bubble >>> ul { padding-left:14px; margin:3px 0; }
.tutor-msg-bubble >>> p { margin:0; }
.tutor-thinking { display:flex; align-items:center; gap:8px; color:#94a3b8; }
.tutor-input-bar { position:sticky; bottom:0; background:#f8fafc; border-top:1px solid #e2e8f0; padding:8px 12px; z-index:100; }
.tutor-input-wrap { display:flex; align-items:flex-end; gap:7px; padding:8px 12px; width:100%; }
.tutor-input { flex:1; padding:7px 10px; border:1.5px solid #e2e8f0; border-radius:8px; font-family:inherit; font-size:12px; color:#1e293b; resize:none; outline:none; line-height:1.5; max-height:80px; }
.tutor-input:focus { border-color:#7c3aed; }
.tutor-send { --f7-button-bg-color:#7c3aed; width:32px !important; height:32px !important; min-width:32px !important; }
</style>
